import { Link, useLocation } from "react-router-dom";
import { FileText, MessageSquare, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PrivacyPolicyDialog } from "@/components/PrivacyPolicy";
import { useI18n } from "@/i18n";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

const GITHUB_URL = "https://github.com/nrminer/Tekoaly-arvostaja";

function NavTab({ to, active, icon: Icon, children }) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "inline-flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-primary-light text-primary dark:text-accent-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">{children}</span>
    </Link>
  );
}

export function SiteLayout({ children }) {
  const { t } = useI18n();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const isInterview = location.pathname.startsWith("/interview");

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <a href="#content" className="skip-to-content">
        Siirry sisältöön
      </a>

      {/* ── Header ── */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-3 rounded-lg outline-none transition-opacity hover:opacity-85"
          >
            <div className="brand-mark shrink-0" aria-hidden="true">
              <FileText className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold leading-tight tracking-tight">
                {t("brand.title")}
              </p>
              <p className="hidden truncate text-xs leading-tight text-muted-foreground md:block">
                {isInterview ? t("interview.brand.sub") : t("brand.subtitle")}
              </p>
            </div>
          </Link>

          <nav aria-label="Site navigation" className="flex items-center gap-1">
            <NavTab to="/" active={!isInterview} icon={FileText}>
              {t("nav.start")}
            </NavTab>
            <NavTab to="/interview" active={isInterview} icon={MessageSquare}>
              Haastattelu
            </NavTab>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label={theme === "dark" ? "Vaihda vaaleaan teemaan" : "Vaihda tummaan teemaan"}
              className="ml-1 h-10 w-10 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {theme === "dark" ? (
                <Sun className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
              ) : (
                <Moon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
              )}
            </Button>
          </nav>
        </div>
      </header>

      {/* ── Page content ── */}
      <div id="content" className="flex flex-1 flex-col">
        {children}
      </div>

      {/* ── Footer ── */}
      <footer className="border-t border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-sm">
              <div className="flex items-center gap-2.5">
                <div className="brand-mark !h-8 !w-8 !rounded-lg" aria-hidden="true">
                  <FileText className="h-4 w-4" strokeWidth={2.25} />
                </div>
                <span className="font-heading text-sm font-semibold">{t("brand.title")}</span>
              </div>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t("footer.tagline")}
              </p>
            </div>

            <div className="flex flex-col gap-2 text-sm">
              <PrivacyPolicyDialog
                trigger={
                  <button className="w-fit text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus:underline focus:outline-none">
                    {t("footer.privacy")}
                  </button>
                }
              />
              <a
                href={GITHUB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-fit items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
                {t("footer.openSource")}
              </a>
            </div>
          </div>

          <p className="mt-8 border-t border-border pt-6 text-xs text-muted-foreground">
            © {new Date().getFullYear()} {t("brand.title")}
          </p>
        </div>
      </footer>
    </div>
  );
}
